import { create } from 'zustand';
import { 
  Team as AppTeam, 
  CreateTeamRequest as AppCreateTeamRequest, 
  UpdateTeamRequest as AppUpdateTeamRequest,
  TeamMemberWithUserInfo,
  UpdateTeamMemberRequest
} from '../../types';
import { 
  Team as ApiTeam, 
  CreateTeamRequest as ApiCreateTeamRequest, 
  UpdateTeamRequest as ApiUpdateTeamRequest,
  createTeam as apiCreateTeam, 
  updateTeam as apiUpdateTeam,
  getUserTeams as apiGetUserTeams,
  getTeam as apiGetTeam,
  getTeamMembersWithUserInfo,
  updateTeamMemberByIds,
  removeTeamMember as apiRemoveTeamMember
} from '../api/teamService';
import { getCurrentUser } from '../auth/session';

// Convert from API Team to App Team
const apiToAppTeam = (apiTeam: ApiTeam): AppTeam => ({
  id: apiTeam.id,
  name: apiTeam.name,
  created_by: apiTeam.createdBy || '', // Map from createdBy to created_by
  created_at: apiTeam.createdAt,
  shifts_per_provider: apiTeam.shiftsPerProvider,
  day_providers_required: apiTeam.dayProvidersRequired,
  night_providers_required: apiTeam.nightProvidersRequired,
  scheduling_period_days: apiTeam.schedulingPeriodDays,
  max_consecutive_shifts: apiTeam.maxConsecutiveShifts,
  min_rest_hours: apiTeam.minRestHours
});

// Convert from App CreateTeamRequest to API CreateTeamRequest
const appToApiCreateTeamRequest = (appRequest: AppCreateTeamRequest): ApiCreateTeamRequest => ({
  name: appRequest.name,
  shiftsPerProvider: appRequest.shifts_per_provider,
  dayProvidersRequired: appRequest.day_providers_required,
  nightProvidersRequired: appRequest.night_providers_required,
  schedulingPeriodDays: appRequest.scheduling_period_days,
  maxConsecutiveShifts: appRequest.max_consecutive_shifts,
  minRestHours: appRequest.min_rest_hours
});

// Convert from App UpdateTeamRequest to API UpdateTeamRequest
const appToApiUpdateTeamRequest = (appRequest: AppUpdateTeamRequest): ApiUpdateTeamRequest => ({
  name: appRequest.name,
  shiftsPerProvider: appRequest.shifts_per_provider,
  dayProvidersRequired: appRequest.day_providers_required,
  nightProvidersRequired: appRequest.night_providers_required,
  schedulingPeriodDays: appRequest.scheduling_period_days,
  maxConsecutiveShifts: appRequest.max_consecutive_shifts,
  minRestHours: appRequest.min_rest_hours
});

interface TeamState {
  teams: AppTeam[];
  currentTeam: AppTeam | null;
  isLoading: boolean;
  error: string | null;
  teamMembers: TeamMemberWithUserInfo[];
  fetchUserTeams: () => Promise<void>;
  createTeam: (data: AppCreateTeamRequest) => Promise<AppTeam>;
  updateTeam: (teamId: string, data: AppUpdateTeamRequest) => Promise<AppTeam>;
  setCurrentTeam: (teamId: string) => void;
  fetchTeamMembers: (teamId: string) => Promise<void>;
  updateTeamMember: (data: UpdateTeamMemberRequest) => Promise<TeamMemberWithUserInfo>;
  removeTeamMember: (teamId: string, userId: string) => Promise<void>;
}

export const useTeamStore = create<TeamState>((set, get) => ({
  teams: [],
  currentTeam: null,
  teamMembers: [],
  isLoading: false,
  error: null,
  
  fetchUserTeams: async () => {
    set({ isLoading: true, error: null });
    try {
      const apiTeams = await apiGetUserTeams();
      const teams = apiTeams.map(apiToAppTeam);
      set({ teams, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
    }
  },
  
  createTeam: async (data: AppCreateTeamRequest) => {
    set({ isLoading: true, error: null });
    try {
      // Get current user ID
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('You must be logged in to create a team');
      }
      
      const apiRequest = appToApiCreateTeamRequest(data);
      // Set the created_by field to the current user's ID
      apiRequest.createdBy = user.id;
      
      const apiNewTeam = await apiCreateTeam(apiRequest);
      const newTeam = apiToAppTeam(apiNewTeam);
      
      set(state => ({ 
        teams: [...state.teams, newTeam],
        currentTeam: newTeam,
        isLoading: false 
      }));
      return newTeam;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  updateTeam: async (teamId: string, data: AppUpdateTeamRequest) => {
    set({ isLoading: true, error: null });
    try {
      const apiRequest = appToApiUpdateTeamRequest(data);
      const apiUpdatedTeam = await apiUpdateTeam(teamId, apiRequest);
      const updatedTeam = apiToAppTeam(apiUpdatedTeam);
      
      set(state => ({
        teams: state.teams.map(team => 
          team.id === teamId ? updatedTeam : team
        ),
        currentTeam: state.currentTeam?.id === teamId ? updatedTeam : state.currentTeam,
        isLoading: false
      }));
      
      return updatedTeam;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },
  
  setCurrentTeam: (teamId: string) => {
    // If we already have the team in our list, use it
    const team = get().teams.find(t => t.id === teamId);
    if (team) {
      console.log('Team found in local store, using it');
      set({ currentTeam: team });
    } else {
      console.log('Team not found in local store, fetching from API');
      set({ isLoading: true, error: null });
      
      // Team not found in local state, try to fetch it
      apiGetTeam(teamId)
        .then(apiTeam => {
          if (!apiTeam) {
            console.warn(`Team with ID ${teamId} not found in the database`);
            set({ 
              currentTeam: null,
              isLoading: false,
              error: `Team not found. Please check if the team with ID ${teamId} exists.` 
            });
            return;
          }
          
          console.log('Team fetched from API:', apiTeam);
          const team = apiToAppTeam(apiTeam);
          
          // Update the store state with the fetched team
          set({ 
            currentTeam: team,
            teams: [...get().teams.filter(t => t.id !== teamId), team], // Add to teams array as well
            isLoading: false 
          });
        })
        .catch(error => {
          console.error('Error fetching team:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to load team', 
            isLoading: false 
          });
        });
    }
  },
  
  fetchTeamMembers: async (teamId: string) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Fetching team members for team ID:', teamId);
      const members = await getTeamMembersWithUserInfo(teamId);
      
      // Log the retrieved members for debugging
      if (members.length === 0) {
        console.warn('No team members found for team:', teamId);
      } else {
        console.log(`Retrieved ${members.length} team members`);
      }
      
      set({ teamMembers: members, isLoading: false });
    } catch (error) {
      console.error('Error fetching team members:', error);
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false,
        // Don't clear the team members array on error
        // This allows previously fetched members to remain visible
      });
    }
  },

  updateTeamMember: async (data: UpdateTeamMemberRequest) => {
    set({ isLoading: true, error: null });
    try {
      const updatedMember = await updateTeamMemberByIds(data);
      
      // Update members list with updated member
      set(state => {
        const updatedMembers = state.teamMembers.map(member => 
          member.team_id === data.team_id && member.user_id === data.user_id
            ? { ...member, ...updatedMember }
            : member
        );
        return { teamMembers: updatedMembers, isLoading: false };
      });
      
      // Need to return a TeamMemberWithUserInfo, so we'll fetch the updated member with user info
      // In a real app, we might want to optimize this
      const updatedMembers = await getTeamMembersWithUserInfo(data.team_id);
      const updatedMemberWithUserInfo = updatedMembers.find(
        m => m.user_id === data.user_id
      ) as TeamMemberWithUserInfo;
      
      return updatedMemberWithUserInfo;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  },

  removeTeamMember: async (teamId: string, userId: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiRemoveTeamMember(teamId, userId);
      
      // Remove member from state
      set(state => ({
        teamMembers: state.teamMembers.filter(
          member => !(member.team_id === teamId && member.user_id === userId)
        ),
        isLoading: false
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
      throw error;
    }
  }
})); 